import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";

const CampaignLinkStats = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [affiliateLinks, setAffiliateLinks] = useState([]);

  const fetchCampaignData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch affiliate links for this campaign
      const response = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${id}/affiliate-links`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const linksData = await response.json();

      if (!linksData.success) {
        throw new Error(linksData.message || 'Failed to fetch affiliate links');
      }    

      // Combine link data with detailed stats
      const combinedData = linksData.data.map((link, index) => ({
        ...link,
      }));
      
      // Sort affiliate links by conversions in descending order
      const sortedLinks = combinedData.sort((a, b) => {
        const conversionsA = a.conversions || 0;
        const conversionsB = b.conversions || 0;
        return conversionsB - conversionsA;
      });
      
      setAffiliateLinks(sortedLinks);
    } catch (error) {
      console.error('Error fetching campaign data:', error);
      toast.error('Failed to fetch campaign data');
    } finally {
      setLoading(false);
    }
  },[id] )

  useEffect(() => {
    fetchCampaignData();
  }, [fetchCampaignData]); // ✅ correct dependency
  

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    }).format(new Date(dateString));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="container mx-auto p-6">
        <div className="flex items-center mb-6 "> 
          <button 
            onClick={() => navigate(-1)} 
            className="text-gray-600 p-2 rounded-full hover:bg-gray-200 lg:mr-4"
          >
            <FaArrowLeft size={22} />
          </button>
          <h2 className="lg:text-2xl text-xl font-bold text-gray-800">
            Campaign Link Statistics
          </h2>
        </div>


        {/* Affiliate Links Table */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="p-6">
            <h3 className="lg:text-xl text-lg font-semibold mb-4">Affiliate Link Performance</h3>
            {affiliateLinks.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No affiliate links generated yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Affiliate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Generated Link
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Visits
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unique Visits
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Conversions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Conversion Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {affiliateLinks.map((link) => {
                      // Handle both data structures: nested affiliate object or flat structure
                      const firstName = link.affiliate?.firstName || link.firstName;
                      const lastName = link.affiliate?.lastName || link.lastName;
                      const username = link.affiliate?.username || link.username;
                      const email = link.affiliate?.email || link.email;
                      const generatedLink = link.generatedLink || '';

                      return (
                        <tr key={link._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {(firstName && firstName !== 'Unknown' && 
                                lastName && lastName !== 'User')
                                ? `${firstName} ${lastName}`
                                : username || 'Unknown User'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {email || 'No email available'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-blue-600 break-all">
                              {generatedLink || 'No link available'}
                            </div>
                          </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(link.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {link.visits || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {link.uniqueVisits || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {link.conversions || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {link.visits > 0 
                            ? `${((link.conversions / link.visits) * 100).toFixed(1)}%`
                            : '0%'}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignLinkStats; 